import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { normalizeOptionalText } from "utils/personOptionalFields";
import { groupClientLocals } from "utils/clientLocals";

const display = (value) => normalizeOptionalText(value) ?? "-";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-UY", { timeZone: "America/Montevideo" });
};

export default function ClientDetailsRow({ person }) {
  const localsLoaded = Array.isArray(person.Locals);
  const locals = localsLoaded ? groupClientLocals(person.Locals) : [];
  const clientFields = [
    ["Cuenta", person.Account],
    ["Dirección", person.Address],
  ];

  return (
    <MDBox
      id={`client-details-${person.Id}`}
      px={{ xs: 2, sm: 3 }}
      py={2.5}
      sx={({ palette }) => ({
        backgroundColor: palette.mode === "dark" ? "#263E5B" : "#F5F9FD",
      })}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <MDBox display="grid" gap={1.25}>
            {clientFields.map(([label, value]) => (
              <MDBox key={label}>
                <MDTypography variant="caption" color="secondary" display="block">
                  {label}
                </MDTypography>
                <MDTypography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {display(value)}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={8}>
          {!localsLoaded ? (
            <MDTypography variant="body2" color="error">
              No se pudieron cargar los locales. Volvé a intentar más tarde.
            </MDTypography>
          ) : locals.length === 0 ? (
            <MDTypography variant="body2" color="secondary">
              No hay locales registrados para este cliente.
            </MDTypography>
          ) : (
            locals.map((local) => {
              const location = [local.address, local.city, local.department]
                .map((value) => normalizeOptionalText(value))
                .filter(Boolean)
                .join(" · ");

              return (
                <MDBox
                  key={local.rootId}
                  p={2}
                  mb={1.5}
                  borderRadius="md"
                  sx={({ palette }) => ({
                    border: `1px solid ${palette.divider}`,
                    backgroundColor: palette.background.paper,
                    overflowWrap: "anywhere",
                  })}
                >
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <MDTypography variant="button" fontWeight="bold">
                      {display(local.name)}
                    </MDTypography>
                    <Chip
                      size="small"
                      label={local.isActive ? "Abierto" : "Cerrado"}
                      color={local.isActive ? "success" : "default"}
                      sx={
                        local.isActive
                          ? { backgroundColor: "#DDF4E4", color: "#257344", fontWeight: 600 }
                          : undefined
                      }
                    />
                  </MDBox>
                  <MDTypography variant="body2" color="secondary" mt={0.5}>
                    {`Dirección: ${location || "-"}`}
                  </MDTypography>
                  <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                    {[...local.periods].reverse().map((period) => (
                      <Box component="li" key={period.Id} sx={{ mb: 0.25 }}>
                        <MDTypography variant="body2">
                          {`${formatDate(period.CreatedAt)} → ${
                            period.IsActive ? "abierto" : formatDate(period.ClosedAt)
                          }`}
                        </MDTypography>
                      </Box>
                    ))}
                  </Box>
                </MDBox>
              );
            })
          )}
        </Grid>
      </Grid>
    </MDBox>
  );
}

ClientDetailsRow.propTypes = {
  person: PropTypes.object.isRequired,
};
